import { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api } from '@/lib/api';
import SEOHead from '@/components/SEOHead';

const BA_CENTER = [-34.6037, -58.3816];
const RADIUS_KM = 8;
const NAVY = '#0b1840';

function distanceKm(lat1, lng1, lat2, lng2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const CATEGORY_META = {
    peluqueria: { label: 'Peluquería Canina', color: '#1e40af', bg: '#eff6ff', icon: '✂️' },
    veterinaria: { label: 'Veterinaria',       color: '#059669', bg: '#ecfdf5', icon: '🩺' },
    ambos:       { label: 'Peluq. & Vet.',     color: '#7c3aed', bg: '#f5f3ff', icon: '🐾' },
};

const FILTERS = [
    { key: 'todos',       label: 'Todos',         icon: '🐾' },
    { key: 'peluqueria',  label: 'Peluquerías',   icon: '✂️' },
    { key: 'veterinaria', label: 'Veterinarias',  icon: '🩺' },
    { key: 'ambos',       label: 'Peluq. & Vet.', icon: '⭐' },
];

const PULSE_CSS = `
@keyframes pulse-ring {
    0%   { transform: scale(1);   opacity: .6; }
    100% { transform: scale(2.2); opacity: 0;  }
}
@keyframes slide-up {
    from { transform: translateY(100%); }
    to   { transform: translateY(0);    }
}
@keyframes fade-in {
    from { opacity: 0; }
    to   { opacity: 1; }
}
@keyframes shimmer {
    0%   { background-position: -200% 0; }
    100% { background-position: 200% 0;  }
}
.cp-marker-pulse { animation: pulse-ring 1.4s ease-out infinite; }
.cp-slide-up     { animation: slide-up 0.32s cubic-bezier(0.16,1,0.3,1) forwards; }
.cp-fade-in      { animation: fade-in 0.2s ease-out forwards; }
.leaflet-container { font-family: inherit; }
.leaflet-control-zoom {
    border: none !important;
    box-shadow: 0 2px 10px rgba(0,0,0,.14) !important;
    border-radius: 12px !important;
    overflow: hidden;
}
.leaflet-control-zoom a {
    width: 36px !important; height: 36px !important;
    line-height: 36px !important; font-size: 18px !important;
    color: #1e293b !important; background: #fff !important;
    border: none !important;
}
.leaflet-control-zoom a:hover { background: #f1f5f9 !important; }
/* hide scrollbar on mobile filter pills */
.cp-pills::-webkit-scrollbar { display: none; }
.cp-pills { -ms-overflow-style: none; scrollbar-width: none; }
`;

function makeMarkerIcon(type, isSelected) {
    const cat = CATEGORY_META[type] || CATEGORY_META.peluqueria;
    const size = isSelected ? 40 : 32;
    const html = `
        <div style="position:relative;display:flex;align-items:center;justify-content:center;">
            ${isSelected ? `<div class="cp-marker-pulse" style="position:absolute;width:${size}px;height:${size}px;border-radius:50%;background:${cat.color};opacity:.3;"></div>` : ''}
            <div style="width:${size}px;height:${size}px;border-radius:50%;background:${cat.color};border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;font-size:${isSelected ? 17 : 14}px;cursor:pointer;">
                ${cat.icon}
            </div>
        </div>`;
    return L.divIcon({ html, className: '', iconSize: [size, size], iconAnchor: [size / 2, size / 2] });
}

function BottomSheet({ loc, cat, onClose }) {
    const waLink = (p) => `https://wa.me/${p.replace(/\D/g, '')}`;
    const mapsLink = `https://www.google.com/maps/dir/?api=1&destination=${loc.lat},${loc.lng}`;
    const photoBgs = [cat.color, cat.bg, '#dbeafe', '#d1fae5'];

    return (
        <>
            <div
                className="cp-fade-in"
                onClick={onClose}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.32)', zIndex: 2000 }}
            />
            <div
                className="cp-slide-up"
                style={{
                    position: 'fixed', bottom: 0, left: 0, right: 0,
                    background: '#fff', borderRadius: '20px 20px 0 0',
                    zIndex: 2001, maxHeight: '65vh', overflowY: 'auto',
                }}
            >
                {/* Drag handle */}
                <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 6px' }}>
                    <div style={{ width: 36, height: 4, borderRadius: 2, background: '#d1d5db' }} />
                </div>

                {/* Photos row */}
                <div className="cp-pills" style={{ display: 'flex', gap: 6, padding: '8px 16px 14px', overflowX: 'auto' }}>
                    {(loc.images?.length > 0 ? loc.images : []).map((img, i) => (
                        <div key={i} style={{
                            width: i === 0 ? 120 : 80, height: 80, borderRadius: 12, flexShrink: 0,
                            overflow: 'hidden',
                        }}>
                            <img src={img} alt={`${loc.name} ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                    ))}
                    {/* Placeholder si no hay imágenes */}
                    {(!loc.images || loc.images.length === 0) && (
                        <div style={{
                            width: 80, height: 80, borderRadius: 12, flexShrink: 0,
                            background: cat.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 30,
                        }}>
                            {cat.icon}
                        </div>
                    )}
                    {/* Slots vacíos hasta 4 */}
                    {Array.from({ length: Math.max(0, 4 - (loc.images?.length || 0) - (loc.images?.length === 0 ? 1 : 0)) }).map((_, i) => (
                        <div key={`ph-${i}`} style={{
                            width: 80, height: 80, borderRadius: 12, flexShrink: 0,
                            background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 22, color: '#d1d5db',
                        }}>
                            📷
                        </div>
                    ))}
                </div>

                {/* Info */}
                <div style={{ padding: '0 16px 32px' }}>
                    {/* Name + badge */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
                        <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: 0, flex: 1, lineHeight: 1.3 }}>
                            {loc.name}
                        </h3>
                        <span style={{
                            flexShrink: 0, fontSize: 11, fontWeight: 600,
                            color: cat.color, background: cat.bg,
                            padding: '4px 10px', borderRadius: 12, marginTop: 2,
                        }}>
                            {cat.label}
                        </span>
                    </div>

                    {/* Info rows */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#4b5563' }}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                            </svg>
                            {loc.address}{loc.neighborhood ? ` · ${loc.neighborhood}` : ''}
                        </div>
                        {loc.phone && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#4b5563' }}>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 015.19 15a19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                                </svg>
                                {loc.phone}
                            </div>
                        )}
                        {loc.description && (
                            <p style={{ fontSize: 13, color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
                                {loc.description}
                            </p>
                        )}
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: 8 }}>
                        {loc.phone && (
                            <a href={`tel:${loc.phone}`} style={{
                                flex: 1, padding: '12px 0', borderRadius: 12,
                                background: NAVY, color: '#fff', fontSize: 13, fontWeight: 600,
                                textDecoration: 'none', textAlign: 'center',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                Llamar
                            </a>
                        )}
                        {loc.whatsapp && (
                            <a href={waLink(loc.whatsapp)} target="_blank" rel="noopener noreferrer" style={{
                                flex: 1, padding: '12px 0', borderRadius: 12,
                                background: '#d1fae5', color: '#065f46', fontSize: 13, fontWeight: 600,
                                textDecoration: 'none', textAlign: 'center',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                WhatsApp
                            </a>
                        )}
                        {loc.lat && loc.lng && (
                            <a href={mapsLink} target="_blank" rel="noopener noreferrer" style={{
                                flex: 1, padding: '12px 0', borderRadius: 12,
                                background: '#dbeafe', color: '#1e3a8a', fontSize: 13, fontWeight: 600,
                                textDecoration: 'none', textAlign: 'center',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                Cómo llegar
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default function LocalesPage() {
    const [locals, setLocals]       = useState([]);
    const [loading, setLoading]     = useState(true);
    const [filter, setFilter]       = useState('todos');
    const [selected, setSelected]   = useState(null);
    const [searchVal, setSearchVal] = useState('');
    const [geocoding, setGeocoding] = useState(false);
    const [userLocation, setUserLocation] = useState(null);
    const [isMobile, setIsMobile]   = useState(() => window.innerWidth < 768);

    const mapRef        = useRef(null);
    const mapInstance   = useRef(null);
    const markersRef    = useRef({});
    const userMarkerRef = useRef(null);
    const cardRefs      = useRef({});

    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    useEffect(() => {
        api.get('/locals')
            .then(r => { setLocals(r.data); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (mapInstance.current || !mapRef.current) return;
        const map = L.map(mapRef.current, { center: BA_CENTER, zoom: 12, zoomControl: false });
        L.control.zoom({ position: isMobile ? 'bottomright' : 'topleft' }).addTo(map);
        L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
            attribution: '&copy; <a href="https://carto.com/">CARTO</a>',
            maxZoom: 19,
        }).addTo(map);
        mapInstance.current = map;
        return () => { map.remove(); mapInstance.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const byType = filter === 'todos'
        ? locals
        : locals.filter(l => l.type === filter || l.type === 'ambos');

    const filtered = userLocation
        ? byType.filter(l =>
            l.lat && l.lng &&
            distanceKm(userLocation.lat, userLocation.lng, l.lat, l.lng) <= RADIUS_KM
          )
        : byType;

    useEffect(() => {
        if (!mapInstance.current) return;
        if (userMarkerRef.current) {
            mapInstance.current.removeLayer(userMarkerRef.current);
            userMarkerRef.current = null;
        }
        if (!userLocation) return;
        const icon = L.divIcon({
            className: '',
            html: `<div style="width:16px;height:16px;border-radius:50%;background:#1a3a8f;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.4);"></div>`,
            iconSize: [16, 16], iconAnchor: [8, 8],
        });
        userMarkerRef.current = L.marker([userLocation.lat, userLocation.lng], { icon, zIndexOffset: 1000 })
            .addTo(mapInstance.current);
    }, [userLocation]);

    useEffect(() => {
        if (!mapInstance.current) return;
        Object.values(markersRef.current).forEach(m => mapInstance.current.removeLayer(m));
        markersRef.current = {};
        filtered.forEach(loc => {
            if (!loc.lat || !loc.lng) return;
            const isSelected = selected === loc.id;
            const icon = makeMarkerIcon(loc.type, isSelected);
            const marker = L.marker([loc.lat, loc.lng], { icon }).addTo(mapInstance.current);
            marker.on('click', () => setSelected(loc.id));
            markersRef.current[loc.id] = marker;
        });
    }, [filtered, selected]);

    useEffect(() => {
        if (!selected || !mapInstance.current) return;
        const loc = locals.find(l => l.id === selected);
        if (loc?.lat && loc?.lng)
            mapInstance.current.flyTo([loc.lat, loc.lng], 15, { duration: 0.6 });
        if (!isMobile)
            cardRefs.current[selected]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [selected, locals, isMobile]);

    const applyLocation = (lat, lng, label) => {
        setUserLocation({ lat, lng, label });
        setSelected(null);
        mapInstance.current?.flyTo([lat, lng], 14, { duration: 0.8 });
    };

    const clearLocation = () => {
        setUserLocation(null);
        setSelected(null);
        mapInstance.current?.flyTo(BA_CENTER, 12, { duration: 0.8 });
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchVal.trim() || !mapInstance.current) return;
        setGeocoding(true);
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchVal + ', Buenos Aires, Argentina')}&limit=1`,
                { headers: { 'Accept-Language': 'es' } }
            );
            const data = await res.json();
            if (data[0]) {
                const label = data[0].display_name?.split(',')[0] || searchVal;
                applyLocation(parseFloat(data[0].lat), parseFloat(data[0].lon), label);
            }
        } catch {}
        setGeocoding(false);
    };

    const handleGeolocate = () => {
        navigator.geolocation?.getCurrentPosition(pos =>
            applyLocation(pos.coords.latitude, pos.coords.longitude, 'Tu ubicación')
        );
    };

    const waLink = (phone) => `https://wa.me/${phone.replace(/\D/g, '')}`;

    const selectedLoc = locals.find(l => l.id === selected);
    const selectedCat = selectedLoc ? (CATEGORY_META[selectedLoc.type] || CATEGORY_META.peluqueria) : null;

    return (
        <>
            <SEOHead
                title="Peluquerías Caninas y Veterinarias en Buenos Aires"
                description="Encontrá peluquerías caninas y veterinarias cerca tuyo en Buenos Aires. Locales verificados con atención profesional para tu mascota."
                url="https://cloudpet.com.ar/locales"
            />
            <style>{PULSE_CSS}</style>

            {/* ── Desktop hero (hidden on mobile) ── */}
            {!isMobile && (
                <div style={{
                    background: 'linear-gradient(135deg, #0f1b4c 0%, #1a3a8f 100%)',
                    padding: '18px 40px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24,
                    flexShrink: 0,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            background: 'rgba(255,255,255,.12)', backdropFilter: 'blur(8px)',
                            padding: '5px 12px', borderRadius: 20,
                            color: 'rgba(255,255,255,.85)', fontSize: 11, fontWeight: 600, flexShrink: 0,
                        }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                            </svg>
                            Locales cerca tuyo
                        </div>
                        <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 800, lineHeight: 1.2, margin: 0 }}>
                            Peluquerías & Veterinarias en Buenos Aires
                        </h1>
                    </div>
                    <p style={{ color: 'rgba(255,255,255,.55)', fontSize: 13, margin: 0, flexShrink: 0 }}>
                        Encontrá profesionales de confianza cerca tuyo
                    </p>
                </div>
            )}

            {/* ── Main container ── */}
            <div style={{
                display: 'flex',
                height: isMobile ? 'calc(100vh - 58px)' : 'calc(100vh - 96px - 58px)',
                minHeight: isMobile ? undefined : 500,
                overflow: 'hidden',
                position: 'relative',
            }}>
                {/* ── Desktop left panel ── */}
                {!isMobile && (
                    <div style={{
                        width: '40%', minWidth: 340,
                        display: 'flex', flexDirection: 'column',
                        borderRight: '1px solid #e2e8f0',
                        background: '#f8f9fb',
                        overflow: 'hidden',
                    }}>
                        {/* Filter tabs */}
                        <div style={{
                            padding: '12px 16px',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            borderBottom: '1px solid #e2e8f0',
                            background: '#fff', flexShrink: 0,
                        }}>
                            <div style={{ display: 'flex', gap: 3, background: '#f1f5f9', borderRadius: 28, padding: 3 }}>
                                {FILTERS.map(f => {
                                    const active = filter === f.key;
                                    return (
                                        <button key={f.key} onClick={() => setFilter(f.key)} style={{
                                            display: 'flex', alignItems: 'center', gap: 5,
                                            padding: '7px 13px', borderRadius: 24,
                                            border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                                            fontSize: 12, fontWeight: active ? 600 : 500,
                                            background: active ? '#3b82f6' : 'transparent',
                                            color: active ? '#fff' : '#64748b',
                                            transition: 'all .2s',
                                            boxShadow: active ? '0 2px 8px rgba(59,130,246,.3)' : 'none',
                                        }}>
                                            <span style={{ fontSize: 12 }}>{f.icon}</span>
                                            {f.label}
                                        </button>
                                    );
                                })}
                            </div>
                            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, flexShrink: 0 }}>
                                {filtered.length} local{filtered.length !== 1 ? 'es' : ''}
                            </span>
                        </div>

                        {/* Zone banner */}
                        {userLocation && (
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '8px 16px',
                                background: '#eff6ff', borderBottom: '1px solid #bfdbfe',
                                flexShrink: 0,
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#1d4ed8', fontWeight: 600 }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                                    </svg>
                                    {userLocation.label} · {RADIUS_KM} km
                                    <span style={{ fontWeight: 400, color: '#3b82f6' }}>
                                        — {filtered.length} local{filtered.length !== 1 ? 'es' : ''}
                                    </span>
                                </div>
                                <button onClick={clearLocation} style={{
                                    display: 'flex', alignItems: 'center', gap: 4,
                                    fontSize: 11, fontWeight: 600, color: '#3b82f6',
                                    background: 'none', border: '1px solid #bfdbfe',
                                    padding: '3px 10px', borderRadius: 20, cursor: 'pointer',
                                    fontFamily: 'inherit',
                                }}>
                                    ✕ Limpiar
                                </button>
                            </div>
                        )}

                        {/* Card list */}
                        <div style={{
                            flex: 1, overflowY: 'auto', padding: 14,
                            display: 'flex', flexDirection: 'column', gap: 10,
                        }}>
                            {loading && [1, 2, 3].map(i => (
                                <div key={i} style={{
                                    height: 130, borderRadius: 16,
                                    background: 'linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)',
                                    backgroundSize: '200% 100%',
                                    animation: 'shimmer 1.4s infinite',
                                }} />
                            ))}

                            {!loading && filtered.length === 0 && (
                                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
                                    <div style={{ fontSize: 44, marginBottom: 12 }}>
                                        {userLocation ? '📍' : '🐾'}
                                    </div>
                                    <p style={{ fontWeight: 600, color: '#64748b', marginBottom: 4 }}>
                                        {userLocation
                                            ? `Sin locales en ${RADIUS_KM} km de esta zona`
                                            : 'No hay locales en esta categoría'}
                                    </p>
                                    <p style={{ fontSize: 13 }}>
                                        {userLocation
                                            ? 'Probá buscar otra dirección o ampliá la búsqueda'
                                            : 'Próximamente agregaremos más locales'}
                                    </p>
                                    {userLocation && (
                                        <button onClick={clearLocation} style={{
                                            marginTop: 14, padding: '8px 18px', borderRadius: 20,
                                            background: '#3b82f6', color: '#fff', border: 'none',
                                            fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                                        }}>
                                            Ver todos los locales
                                        </button>
                                    )}
                                </div>
                            )}

                            {filtered.map(loc => {
                                const cat = CATEGORY_META[loc.type] || CATEGORY_META.peluqueria;
                                const isActive = selected === loc.id;
                                return (
                                    <div
                                        key={loc.id}
                                        ref={el => { cardRefs.current[loc.id] = el; }}
                                        onClick={() => setSelected(isActive ? null : loc.id)}
                                        style={{
                                            background: '#fff', borderRadius: 16, padding: '16px 18px',
                                            cursor: 'pointer', transition: 'all .22s',
                                            boxShadow: isActive
                                                ? '0 0 0 2px #3b82f6, 0 8px 24px rgba(59,130,246,.15)'
                                                : '0 1px 4px rgba(0,0,0,.06)',
                                            borderLeft: isActive ? '4px solid #3b82f6' : '4px solid transparent',
                                            transform: isActive ? 'translateX(2px)' : 'none',
                                        }}
                                        onMouseEnter={e => {
                                            if (!isActive) {
                                                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,.1)';
                                                e.currentTarget.style.borderLeft = '4px solid rgba(59,130,246,.25)';
                                            }
                                        }}
                                        onMouseLeave={e => {
                                            if (!isActive) {
                                                e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,.06)';
                                                e.currentTarget.style.borderLeft = '4px solid transparent';
                                            }
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                                            <div style={{ flex: 1, minWidth: 0, marginRight: 10 }}>
                                                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 6px', lineHeight: 1.3 }}>
                                                    {loc.name}
                                                </h3>
                                                <span style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                                    fontSize: 11, fontWeight: 600,
                                                    color: cat.color, background: cat.bg,
                                                    padding: '3px 10px', borderRadius: 12,
                                                }}>
                                                    {cat.icon} {cat.label}
                                                </span>
                                            </div>
                                            <div style={{
                                                width: 44, height: 44, borderRadius: 11, flexShrink: 0,
                                                background: `linear-gradient(135deg, ${cat.bg}, ${cat.color}18)`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: 18, overflow: 'hidden',
                                            }}>
                                                {(loc.images?.[0] || loc.image)
                                                    ? <img src={loc.images?.[0] || loc.image} alt={loc.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    : cat.icon
                                                }
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
                                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                                                <circle cx="12" cy="10" r="3"/>
                                            </svg>
                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {loc.address}{loc.neighborhood ? ` · ${loc.neighborhood}` : ''}
                                            </span>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            {loc.phone && (
                                                <a href={`tel:${loc.phone}`} onClick={e => e.stopPropagation()} style={{
                                                    display: 'flex', alignItems: 'center', gap: 4,
                                                    fontSize: 11, fontWeight: 500, color: '#475569',
                                                    textDecoration: 'none', background: '#f1f5f9',
                                                    padding: '6px 12px', borderRadius: 20,
                                                }}>
                                                    📞 Llamar
                                                </a>
                                            )}
                                            {loc.whatsapp && (
                                                <a
                                                    href={waLink(loc.whatsapp)}
                                                    target="_blank" rel="noopener noreferrer"
                                                    onClick={e => e.stopPropagation()}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: 4,
                                                        fontSize: 11, fontWeight: 600, color: '#fff',
                                                        textDecoration: 'none', background: '#25d366',
                                                        padding: '6px 12px', borderRadius: 20,
                                                    }}
                                                >
                                                    WhatsApp
                                                </a>
                                            )}
                                            <button
                                                onClick={e => { e.stopPropagation(); setSelected(isActive ? null : loc.id); }}
                                                style={{
                                                    marginLeft: 'auto',
                                                    background: isActive ? '#3b82f6' : 'rgba(59,130,246,.1)',
                                                    color: isActive ? '#fff' : '#3b82f6',
                                                    border: 'none', padding: '6px 14px', borderRadius: 20,
                                                    fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                                                    transition: 'all .18s',
                                                }}
                                            >
                                                Ver en mapa
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* ── Map area ── */}
                <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                    <div ref={mapRef} style={{ width: '100%', height: '100%' }} />

                    {/* ── Mobile floating UI ── */}
                    {isMobile && (
                        /* Wrapper encima de todo Leaflet — pointer-events:none para no bloquear el mapa */
                        <div style={{
                            position: 'absolute', inset: 0,
                            zIndex: 1500,
                            pointerEvents: 'none',
                        }}>
                            {/* Search bar */}
                            <form
                                onSubmit={handleSearch}
                                style={{
                                    pointerEvents: 'auto',
                                    position: 'absolute', top: 10, left: 12, right: 12,
                                    display: 'flex', alignItems: 'center',
                                    background: '#fff',
                                    borderRadius: 48,
                                    padding: '5px 5px 5px 16px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,.18)',
                                    border: '1px solid #e2e8f0',
                                    gap: 4,
                                }}
                            >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" style={{ flexShrink: 0 }}>
                                    <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                                </svg>
                                <input
                                    value={searchVal}
                                    onChange={e => setSearchVal(e.target.value)}
                                    placeholder="Ingresá tu dirección o barrio..."
                                    style={{
                                        flex: 1, border: 'none', background: 'none', outline: 'none',
                                        color: '#1e293b', fontSize: 13, padding: '8px 6px',
                                        fontFamily: 'inherit', minWidth: 0,
                                    }}
                                />
                                <button type="submit" disabled={geocoding} style={{
                                    flexShrink: 0,
                                    background: '#3b82f6', border: 'none', color: '#fff',
                                    padding: '8px 16px', borderRadius: 40,
                                    fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                                    opacity: geocoding ? .7 : 1, transition: 'opacity .15s',
                                }}>
                                    {geocoding ? '...' : 'Buscar'}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleGeolocate}
                                    title="Usar mi ubicación"
                                    style={{
                                        width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                                        border: 'none', background: '#f1f5f9', color: '#64748b',
                                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}
                                >
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"/>
                                        <circle cx="12" cy="12" r="3"/>
                                        <line x1="12" y1="2" x2="12" y2="5"/>
                                        <line x1="12" y1="19" x2="12" y2="22"/>
                                        <line x1="2" y1="12" x2="5" y2="12"/>
                                        <line x1="19" y1="12" x2="22" y2="12"/>
                                    </svg>
                                </button>
                            </form>

                            {/* Filter pills */}
                            <div
                                className="cp-pills"
                                style={{
                                    pointerEvents: 'auto',
                                    position: 'absolute', top: 70, left: 0, right: 0,
                                    display: 'flex', gap: 6,
                                    padding: '0 12px', overflowX: 'auto',
                                }}
                            >
                                {FILTERS.map(f => {
                                    const active = filter === f.key;
                                    return (
                                        <button key={f.key} onClick={() => setFilter(f.key)} style={{
                                            flexShrink: 0, padding: '8px 16px', borderRadius: 20, border: 'none',
                                            fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                                            transition: 'all .2s',
                                            background: active ? NAVY : 'rgba(255,255,255,.92)',
                                            backdropFilter: active ? 'none' : 'blur(16px)',
                                            WebkitBackdropFilter: active ? 'none' : 'blur(16px)',
                                            color: active ? '#fff' : '#374151',
                                            boxShadow: '0 2px 8px rgba(0,0,0,.08)',
                                        }}>
                                            {f.icon} {f.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Zone banner */}
                            {userLocation && (
                                <div style={{
                                    pointerEvents: 'auto',
                                    position: 'absolute', top: 122, left: 12, right: 12,
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    background: 'rgba(239,246,255,.95)',
                                    backdropFilter: 'blur(12px)',
                                    WebkitBackdropFilter: 'blur(12px)',
                                    borderRadius: 20, padding: '7px 14px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,.1)',
                                }}>
                                    <span style={{ fontSize: 12, color: '#1d4ed8', fontWeight: 600 }}>
                                        📍 {userLocation.label} · {RADIUS_KM} km
                                        <span style={{ fontWeight: 400, color: '#3b82f6' }}> — {filtered.length} locales</span>
                                    </span>
                                    <button onClick={clearLocation} style={{
                                        fontSize: 11, color: '#3b82f6', background: 'none', border: 'none',
                                        cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit',
                                    }}>
                                        ✕ Limpiar
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Desktop search bar overlay ── */}
                    {!isMobile && (
                        <form
                            onSubmit={handleSearch}
                            style={{
                                position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)',
                                zIndex: 1000,
                                display: 'flex', alignItems: 'center',
                                background: '#fff', borderRadius: 48,
                                padding: '5px 5px 5px 16px',
                                boxShadow: '0 4px 20px rgba(0,0,0,.12)',
                                border: '1px solid #e2e8f0',
                                width: 'min(90%, 460px)', gap: 4,
                            }}
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" style={{ flexShrink: 0 }}>
                                <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                            </svg>
                            <input
                                value={searchVal}
                                onChange={e => setSearchVal(e.target.value)}
                                placeholder="Ingresá tu dirección o barrio..."
                                style={{
                                    flex: 1, border: 'none', background: 'none', outline: 'none',
                                    color: '#1e293b', fontSize: 13, padding: '8px 8px',
                                    fontFamily: 'inherit',
                                }}
                            />
                            <button type="submit" disabled={geocoding} style={{
                                background: '#3b82f6', border: 'none', color: '#fff',
                                padding: '8px 18px', borderRadius: 40,
                                fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                                opacity: geocoding ? .7 : 1, transition: 'opacity .15s',
                            }}>
                                {geocoding ? '...' : 'Buscar'}
                            </button>
                            <button
                                type="button"
                                onClick={handleGeolocate}
                                title="Usar mi ubicación"
                                style={{
                                    width: 36, height: 36, borderRadius: '50%',
                                    border: 'none', background: '#f1f5f9', color: '#64748b',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0, transition: 'background .15s',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#e2e8f0'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = '#f1f5f9'; }}
                            >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/>
                                    <circle cx="12" cy="12" r="3"/>
                                    <line x1="12" y1="2" x2="12" y2="5"/>
                                    <line x1="12" y1="19" x2="12" y2="22"/>
                                    <line x1="2" y1="12" x2="5" y2="12"/>
                                    <line x1="19" y1="12" x2="22" y2="12"/>
                                </svg>
                            </button>
                        </form>
                    )}
                </div>
            </div>

            {/* ── Mobile bottom sheet ── */}
            {isMobile && selected && selectedLoc && selectedCat && (
                <BottomSheet
                    loc={selectedLoc}
                    cat={selectedCat}
                    onClose={() => setSelected(null)}
                />
            )}
        </>
    );
}
